import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpInterceptorFn } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { VisitsResponse, QueueSummary, DriverVisit, DriverStatus, TransporterCompany, CreateVisitDto, PlanningResponse, ImportResult, PlannedArrival, AddPlanningDto, PlanningStats, VoiceParseResponse, Site, SiteUnit, Article, QuantityUnit, Client, EpalementCalculateResult, WeeklyPlanning, FormConfig, DriverMessage, KpiSnapshotResponse } from '../models';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (req.url.includes('/health')) return next(req);

  return new Observable((subscriber) => {
    auth.getIdToken().then((token) => {
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const siteOverride = auth.siteOverride();
      if (siteOverride && siteOverride !== auth.currentSite()) headers['X-Site-Override'] = siteOverride;
      const cloned = req.clone({ setHeaders: headers });
      next(cloned).subscribe({
        next: (event) => subscriber.next(event),
        error: (err) => {
          if (err.status === 401) { auth.logout(); router.navigate(['/login']); }
          subscriber.error(err);
        },
        complete: () => subscriber.complete(),
      });
    }).catch(() => {
      auth.logout();
      router.navigate(['/login']);
      subscriber.error(new Error('No token'));
    });
  });
};

@Injectable({ providedIn: 'root' })
export class ApiService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private base = environment.apiUrl;

  // Visits
  getVisits(date?: string, unit?: string): Observable<VisitsResponse> {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (unit) params.set('unit', unit);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.http.get<VisitsResponse>(`${this.base}/visits${qs}`);
  }

  getVisitsSummary(): Observable<QueueSummary> {
    return this.http.get<QueueSummary>(`${this.base}/visits/summary`);
  }

  createVisit(dto: CreateVisitDto): Observable<{ visit: DriverVisit }> {
    return this.http.post<{ visit: DriverVisit }>(`${this.base}/visits`, dto);
  }

  lookupOrder(orderNumber: string): Observable<{
    found: boolean;
    item?: {
      orderNumber: string;
      company?: string | null;
      plate?: string | null;
      unit?: string | null;
      product?: string | null;
      articleCode?: string | null;
      chassisPlate?: string | null;
      containerPlate?: string | null;
      deliveryNumber?: string | null;
      transportNumber?: string | null;
      notes?: string | null;
      planDate?: string | null;
      scheduledAt?: string | null;
      quantity?: number | null;
      quantityUnit?: string | null;
      flowType?: 'expedition' | 'livraison';
    };
  }> {
    return this.http.get<{
      found: boolean;
      item?: {
        orderNumber: string;
        company?: string | null;
        plate?: string | null;
        unit?: string | null;
        product?: string | null;
        articleCode?: string | null;
        chassisPlate?: string | null;
        containerPlate?: string | null;
        deliveryNumber?: string | null;
        transportNumber?: string | null;
        notes?: string | null;
        planDate?: string | null;
        scheduledAt?: string | null;
        quantity?: number | null;
        quantityUnit?: string | null;
        flowType?: 'expedition' | 'livraison';
      };
    }>(`${this.base}/visits/lookup-order`, {
      params: { orderNumber },
    });
  }

  getUnassignedOrders(q?: string): Observable<{
    items: Array<{
      id: string;
      orderNumber: string;
      unit: string | null;
      company: string | null;
      plate: string | null;
      notes?: string | null;
      scheduledAt?: string | null;
      planDate?: string | null;
      product?: string | null;
      articleCode?: string | null;
      chassisPlate?: string | null;
      containerPlate?: string | null;
      deliveryNumber?: string | null;
      transportNumber?: string | null;
      quantity?: number | null;
      quantityUnit?: string | null;
      flowType?: 'expedition' | 'livraison';
    }>;
  }> {
    const params: Record<string, string> = {};
    if (q?.trim()) params['q'] = q.trim();
    return this.http.get<{ items: any[] }>(`${this.base}/visits/unassigned-orders`, { params });
  }

  updateVisit(id: string, dto: Partial<any>): Observable<{ visit: DriverVisit; plannedDeleted?: boolean; linkedOrder?: boolean }> {
    return this.http.put<{ visit: DriverVisit; plannedDeleted?: boolean; linkedOrder?: boolean }>(`${this.base}/visits/${id}`, dto);
  }

  deleteVisit(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.base}/visits/${id}`);
  }

  updateStatus(id: string, status: DriverStatus): Observable<{ visit: DriverVisit }> {
    return this.http.patch<{ visit: DriverVisit }>(`${this.base}/visits/${id}/status`, { status });
  }

  validateSecurity(id: string): Observable<{ visit: DriverVisit }> {
    return this.http.patch<{ visit: DriverVisit }>(`${this.base}/visits/${id}/security`, {});
  }

  reorderVisits(orderedIds: string[]): Observable<{ reordered: number }> {
    return this.http.patch<{ reordered: number }>(`${this.base}/visits/reorder`, { orderedIds });
  }

  acknowledgeHelp(id: string): Observable<{ acknowledged: boolean }> {
    return this.http.patch<{ acknowledged: boolean }>(`${this.base}/visits/${id}/acknowledge-help`, {});
  }

  setEstimatedPassage(id: string, time: string): Observable<{ visit: DriverVisit }> {
    return this.http.patch<{ visit: DriverVisit }>(`${this.base}/visits/${id}/estimated-passage`, { estimatedPassageAt: time });
  }

  cancelVisit(id: string): Observable<{ visit: DriverVisit; planningCancelled: boolean }> {
    return this.http.patch<{ visit: DriverVisit; planningCancelled: boolean }>(`${this.base}/visits/${id}/cancel`, {});
  }

  applyQueueOrder(order: string[]): Observable<{ applied: number }> {
    return this.http.patch<{ applied: number }>(`${this.base}/visits/apply-order`, { order });
  }

  getPhotoUploadUrl(filename: string): Observable<{ uploadUrl: string; s3Key: string }> {
    return this.http.get<{ uploadUrl: string; s3Key: string }>(`${this.base}/visits/photo-upload-url?filename=${encodeURIComponent(filename)}`);
  }

  getPostRegInstructionUploadUrl(filename: string): Observable<{ uploadUrl: string; s3Key: string }> {
    return this.http.get<{ uploadUrl: string; s3Key: string }>(
      `${this.base}/sites/current/post-reg-instruction-upload-url?filename=${encodeURIComponent(filename)}`,
    );
  }

  async uploadPostRegInstructionImage(file: File): Promise<string> {
    const { uploadUrl, s3Key } = await new Promise<{ uploadUrl: string; s3Key: string }>((resolve, reject) => {
      this.getPostRegInstructionUploadUrl(file.name).subscribe({ next: resolve, error: reject });
    });
    await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'image/png' } });
    return s3Key;
  }

  // Transporters
  searchTransporters(search: string): Observable<{ items: TransporterCompany[]; total: number }> {
    return this.http.get<{ items: TransporterCompany[]; total: number }>(`${this.base}/transporters?search=${encodeURIComponent(search)}`);
  }

  getTransporters(): Observable<{ items: TransporterCompany[]; total: number }> {
    return this.http.get<{ items: TransporterCompany[]; total: number }>(`${this.base}/transporters`);
  }

  getTransportersAll(): Observable<{ items: TransporterCompany[]; total: number }> {
    return this.http.get<{ items: TransporterCompany[]; total: number }>(`${this.base}/transporters?all=true`);
  }

  createTransporter(name: string): Observable<{ id: string; name: string; created: boolean; penalty_per_hour?: number }> {
    return this.http.post<{ id: string; name: string; created: boolean }>(`${this.base}/transporters`, { name });
  }

  deactivateTransporter(id: string): Observable<{ id: string; deactivated: boolean }> {
    return this.http.delete<{ id: string; deactivated: boolean }>(`${this.base}/transporters/${id}`);
  }

  updateTransporter(id: string, dto: { name?: string; active?: boolean }): Observable<any> {
    return this.http.put(`${this.base}/transporters/${id}`, dto);
  }

  getTransporterContracts(): Observable<{ contracts: { transporter_id: string; penalty_per_hour: number }[]; default_penalty_per_hour?: number }> {
    return this.http.get<{ contracts: { transporter_id: string; penalty_per_hour: number }[]; default_penalty_per_hour?: number }>(`${this.base}/transporters/contracts`);
  }

  saveTransporterContracts(
    contracts: { transporter_id: string; penalty_per_hour: number }[],
    defaultPenaltyPerHour?: number,
  ): Observable<any> {
    const body: any = { contracts };
    if (defaultPenaltyPerHour !== undefined) body.default_penalty_per_hour = defaultPenaltyPerHour;
    return this.http.put(`${this.base}/transporters/contracts`, body);
  }

  // History
  getHistory(from?: string, to?: string): Observable<{ items: DriverVisit[]; total: number }> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.http.get<{ items: DriverVisit[]; total: number }>(`${this.base}/history${qs}`);
  }

  getHistoryCsv(from?: string, to?: string): Observable<string> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    params.set('format', 'csv');
    return this.http.get(`${this.base}/history?${params.toString()}`, { responseType: 'text' });
  }

  // Planning
  getPlanningToday(unit?: string): Observable<PlanningResponse> {
    const qs = unit ? `?unit=${unit}` : '';
    return this.http.get<PlanningResponse>(`${this.base}/planning/today${qs}`);
  }

  getPlanningStats(): Observable<PlanningStats> {
    return this.http.get<PlanningStats>(`${this.base}/planning/stats`);
  }

  importPlanning(csv: string): Observable<ImportResult> {
    return this.http.post<ImportResult>(`${this.base}/planning/import`, { csv });
  }

  importPlanningXlsx(file: File, type: 'expedition' | 'livraison' = 'expedition'): Observable<any> {
    return new Observable(subscriber => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = btoa(new Uint8Array(reader.result as ArrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
        this.http.post(`${this.base}/planning/import`, { xlsx: base64, type }).subscribe({
          next: (res) => { subscriber.next(res); subscriber.complete(); },
          error: (err) => subscriber.error(err),
        });
      };
      reader.readAsArrayBuffer(file);
    });
  }

  addPlannedArrival(dto: AddPlanningDto): Observable<{ item: PlannedArrival }> {
    return this.http.post<{ item: PlannedArrival }>(`${this.base}/planning`, dto);
  }

  updatePlannedArrival(id: string, dto: Partial<AddPlanningDto>): Observable<{ item: PlannedArrival }> {
    return this.http.put<{ item: PlannedArrival }>(`${this.base}/planning/${id}`, dto);
  }

  deletePlannedArrival(id: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.base}/planning/${id}`);
  }

  cancelPlannedArrival(id: string): Observable<{ cancelled: boolean }> {
    return this.http.patch<{ cancelled: boolean }>(`${this.base}/planning/${id}/cancel`, {});
  }

  reactivatePlannedArrival(id: string): Observable<{ reactivated: boolean }> {
    return this.http.patch<{ reactivated: boolean }>(`${this.base}/planning/${id}/reactivate`, {});
  }

  getWeeklyPlanning(from?: string, to?: string, includeCancelled = false): Observable<WeeklyPlanning> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (includeCancelled) params.set('include_cancelled', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.http.get<WeeklyPlanning>(`${this.base}/planning${qs}`);
  }

  // Voice
  parseVoice(transcript: string): Observable<VoiceParseResponse> {
    return this.http.post<VoiceParseResponse>(`${this.base}/planning/parse-voice`, { transcript });
  }

  // Units / org tree (Secteur → Unité → Poste)
  getUnits(): Observable<{ items: SiteUnit[] }> {
    return this.http.get<{ items: SiteUnit[] }>(`${this.base}/units`);
  }

  getOrgTree(): Observable<{ tree: SiteUnit[]; items: SiteUnit[] }> {
    return this.http.get<{ tree: SiteUnit[]; items: SiteUnit[] }>(`${this.base}/units?tree=1`);
  }

  createUnit(dto: {
    code?: string;
    label: string;
    kind?: 'secteur' | 'unite' | 'poste';
    parentId?: string | null;
    sort_order?: number;
  }): Observable<{ item: SiteUnit }> {
    return this.http.post<{ item: SiteUnit }>(`${this.base}/units`, dto);
  }

  updateUnit(id: string, dto: Partial<SiteUnit> & { code?: string }): Observable<{ item: SiteUnit }> {
    return this.http.put<{ item: SiteUnit }>(`${this.base}/units/${id}`, dto);
  }

  deleteUnit(id: string): Observable<{ deleted: boolean; count?: number }> {
    return this.http.delete<{ deleted: boolean; count?: number }>(`${this.base}/units/${id}`);
  }

  // Articles (products + density)
  getArticles(params?: { q?: string; code?: string; all?: boolean }): Observable<{ items: Article[]; total?: number; item?: Article }> {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.code) qs.set('code', params.code);
    if (params?.all) qs.set('all', 'true');
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.http.get<{ items: Article[]; total?: number; item?: Article }>(`${this.base}/articles${suffix}`);
  }

  createArticle(dto: { code: string; designation: string; density?: number | null; posteIds?: string[] }): Observable<{ item: Article }> {
    return this.http.post<{ item: Article }>(`${this.base}/articles`, dto);
  }

  updateArticle(id: string, dto: Partial<{ code: string; designation: string; density: number | null; active: boolean; posteIds: string[] }>): Observable<{ item: Article }> {
    return this.http.patch<{ item: Article }>(`${this.base}/articles/${id}`, dto);
  }

  calculateEpalement(dto: {
    capacityLiters: number;
    density: number;
    hasBaffles?: boolean;
    fillRate?: number;
  }): Observable<EpalementCalculateResult> {
    return this.http.post<EpalementCalculateResult>(`${this.base}/articles/epalement/calculate`, dto);
  }

  getQuantityUnits(): Observable<{ items: QuantityUnit[] }> {
    return this.http.get<{ items: QuantityUnit[] }>(`${this.base}/quantity-units`);
  }

  // Clients
  getClients(params?: { q?: string; all?: boolean }): Observable<{ items: Client[]; total?: number }> {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    if (params?.all) qs.set('all', 'true');
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.http.get<{ items: Client[]; total?: number }>(`${this.base}/clients${suffix}`);
  }

  createClient(name: string): Observable<{ item: Client }> {
    return this.http.post<{ item: Client }>(`${this.base}/clients`, { name });
  }

  updateClient(id: string, dto: Partial<{ name: string; active: boolean }>): Observable<{ item: Client }> {
    return this.http.patch<{ item: Client }>(`${this.base}/clients/${id}`, dto);
  }

  // Sites
  getCurrentSite(): Observable<Site> {
    return this.http.get<Site>(`${this.base}/sites/current`);
  }

  updateCurrentSite(dto: Partial<Site>): Observable<Site> {
    return this.http.put<Site>(`${this.base}/sites/current`, dto);
  }

  updateFormConfig(config: FormConfig): Observable<Site> {
    return this.http.put<Site>(`${this.base}/sites/current`, { form_config: config });
  }

  getAllSites(): Observable<{ items: Site[] }> {
    return this.http.get<{ items: Site[] }>(`${this.base}/sites`);
  }

  createSite(dto: { code: string; name: string; lat?: number; lng?: number }): Observable<Site> {
    return this.http.post<Site>(`${this.base}/sites`, dto);
  }

  deleteSite(code: string): Observable<{ deleted: boolean }> {
    return this.http.delete<{ deleted: boolean }>(`${this.base}/sites/${code}`, {
      headers: { 'X-Confirm-Delete': `DELETE-SITE-${code}` },
    });
  }

  // Messages BCU
  sendMessage(visitId: string, content: string): Observable<{ message: DriverMessage; translatedTo: string }> {
    return this.http.post<{ message: DriverMessage; translatedTo: string }>(`${this.base}/visits/${visitId}/messages`, { content });
  }

  getMessages(visitId: string): Observable<{ messages: DriverMessage[]; total: number }> {
    return this.http.get<{ messages: DriverMessage[]; total: number }>(`${this.base}/visits/${visitId}/messages`);
  }

  // Incident
  activateIncident(): Observable<{ incidentActive: boolean }> {
    return this.http.post<{ incidentActive: boolean }>(`${this.base}/sites/current/incident/activate`, {});
  }

  deactivateIncident(): Observable<{ incidentActive: boolean }> {
    return this.http.post<{ incidentActive: boolean }>(`${this.base}/sites/current/incident/deactivate`, {});
  }

  // Analytics
  getAnalytics(period: 'today'|'week'|'month'|'ytd' = 'today', scope: 'site'|'global' = 'site'): Observable<KpiSnapshotResponse> {
    return this.http.get<KpiSnapshotResponse>(`${this.base}/analytics?period=${period}&scope=${scope}`);
  }

  getAnalyticsHistory(period: 'today'|'week'|'month'|'ytd', limit = 14): Observable<{ snapshots: any[] }> {
    return this.http.get<{ snapshots: any[] }>(`${this.base}/analytics/history?period=${period}&limit=${limit}`);
  }

  triggerKpiCompute(): Observable<any> {
    return this.http.post<any>(`${this.base}/admin/compute-kpis`, {});
  }

  getAdminMetrics(): Observable<any> {
    return this.http.get<any>(`${this.base}/admin/metrics`);
  }

  // Photo upload
  async uploadPhoto(file: File): Promise<string> {
    const { uploadUrl, s3Key } = await new Promise<{ uploadUrl: string; s3Key: string }>((resolve, reject) => {
      this.getPhotoUploadUrl(file.name).subscribe({ next: resolve, error: reject });
    });
    await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    return s3Key;
  }
}
